import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, SearchIcon } from 'lucide-react';
import { Product } from '../../types/catalog';
import { materialDirectory, supplierDirectory } from '../../utils/catalogIndex';
import { MaterialsDirectory } from './MaterialsDirectory';
import { SuppliersDirectory } from './SuppliersDirectory';
type Tab = 'materials' | 'suppliers';
interface CatalogBrowserProps {
  onCompare: (product: Product) => void;
}
export function CatalogBrowser({
  onCompare
}: CatalogBrowserProps) {
  const [tab, setTab] = useState<Tab | null>(null);
  const [filter, setFilter] = useState('');
  const counts = useMemo(() => ({
    materials: materialDirectory().reduce((sum, group) => sum + group.items.length, 0),
    suppliers: supplierDirectory().length
  }), []);
  function toggle(next: Tab) {
    setTab(current => current === next ? null : next);
    setFilter('');
  }
  const tabs: {
    id: Tab;
    label: string;
  }[] = [{
    id: 'materials',
    label: 'Materials'
  }, {
    id: 'suppliers',
    label: 'Suppliers'
  }];
  return <section className="w-full" aria-label="Browse the price book" data-id="element-2" data-mp="fu0mjjioc4aq">
      <div className="grid grid-cols-2 gap-2" data-id="element-3" data-mp="avpcp8sdptsi">
        {tabs.map(({
        id,
        label
      }) => {
        const active = tab === id;
        return <button key={id} type="button" onClick={() => toggle(id)} aria-expanded={active} aria-controls="catalog-panel" className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors duration-150 ${active ? 'border-ink bg-ink text-white' : 'border-line bg-surface text-ink hover:border-ink-soft'}`} data-id="element-4" data-mp="5yfjrj5s6zzx">
              <span className="min-w-0" data-id="element-5" data-mp="3eal3mpzdf8y1">
                <span className="block text-sm font-medium" data-id="element-6" data-mp="39d35lumw6e4t">{label}</span>
                <span className={`tnum block text-xs ${active ? 'text-white/70' : 'text-ink-soft'}`} data-id="element-7" data-mp="39fxlr9yvhcsf">
                  {counts[id]} listed
                </span>
              </span>
              <ChevronDownIcon className={`h-4 w-4 shrink-0 transition-transform duration-200 ${active ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-8" data-mp="1rg81wizdgrns" />
            </button>;
      })}
      </div>

      <AnimatePresence initial={false} data-id="element-9" data-mp="1hobw0lr1nx90">
        {tab && <motion.div key={tab} id="catalog-panel" initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-2 overflow-hidden rounded-xl border border-line bg-surface" data-id="element-10" data-mp="1mig10zgr1v7g">
            <label className="flex items-center gap-2 border-b border-line px-5 py-2.5" data-id="element-11" data-mp="13qn9l9hbwi1u">
              <SearchIcon className="h-4 w-4 shrink-0 text-ink-soft" aria-hidden="true" data-id="element-12" data-mp="k426vam7ehgy" />
              <span className="sr-only" data-id="element-13" data-mp="g39wge1li6j3">Filter {tab}</span>
              <input type="search" value={filter} onChange={event => setFilter(event.target.value)} placeholder={tab === 'materials' ? 'Filter materials…' : 'Filter by name or city…'} autoComplete="off" autoCorrect="off" spellCheck={false} className="h-8 w-full bg-transparent text-sm text-ink outline-none placeholder:text-ink-soft" data-id="element-14" data-mp="g4tby2ffgdjt" />
            </label>
            <div className="max-h-[60vh] overflow-y-auto overscroll-contain" data-id="element-15" data-mp="1cshgfhmkg7kw">
              {tab === 'materials' ? <MaterialsDirectory filter={filter} onCompare={onCompare} data-id="element-16" data-mp="17wiq3ohogfhu" /> : <SuppliersDirectory filter={filter} onCompare={onCompare} data-id="element-17" data-mp="1mp2n07e8hqqu" />}
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}